import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import AppLayout from "../layouts/AppLayout"; // ✅ IMPORT THE COMMON LAYOUT
import "./Profile.css";
import EmojiPicker from "emoji-picker-react";
import { Link, useParams } from "react-router-dom"; // ✅ Combined import
import ProfileEditDialog from "./ProfileEditDialog";
import PostDetailModal from "./PostDetailModal";
import { timeAgo } from "../utils/timeAgo";


// ✅ Helper to normalize post counts safely
function normalizePost(post) {
  return {
    ...post,
    // ✅ Keep backend counts if provided; only fallback if missing
    repliesCount: post.repliesCount ?? (post.replies?.length || 0),
    repostsCount: post.repostsCount ?? (Array.isArray(post.reposts) ? post.reposts.length : 0),
    likes: post.likes ?? (Array.isArray(post.likesList) ? post.likesList.length : 0),
    thumbnail: post.thumbnail?.startsWith("http")
      ? post.thumbnail
      : `${process.env.REACT_APP_API_URL}${post.thumbnail || "/images/default-thumbnail.jpg"}`,
    media_url: post.media_url ? `${process.env.REACT_APP_API_URL}${post.media_url}` : null,
    gif: post.gif_url || null,
  };
}

function Profile({ setCurrentUser }) {
  const [currentUser, setLocalCurrentUser] = useState(null);
  const [profilePicture, setProfilePicture] = useState("");
  const [profileBackground, setProfileBackground] = useState(""); // ✅ New state for background image
  const [imageKey, setImageKey] = useState(Date.now());
  const [profilePosts, setProfilePosts] = useState([]);
  const [profileSearchQuery, setProfileSearchQuery] = useState("");
  const [profilePostContent, setProfilePostContent] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const postInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { username } = useParams();
  const [filterType, setFilterType] = useState("all");
  const [likedPosts, setLikedPosts] = useState([]);
  const [userReplies, setUserReplies] = useState([]);
  const [userReposts, setUserReposts] = useState([]);
  const originalRefs = useRef({});
  const replyRefs = useRef({});
  const [lineHeights, setLineHeights] = useState({});
  const [refsReady, setRefsReady] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [followModalType, setFollowModalType] = useState(""); // "followers" or "following"
  const [followModalList, setFollowModalList] = useState([]);

  
  useEffect(() => {
    if (userReplies.length === 0) return;

    const allReady = userReplies.every(
      (reply) => originalRefs.current[reply.id] && replyRefs.current[reply.id]
    );

    if (!allReady) {
      console.warn("🛑 Refs not ready, retrying in 100ms...");
      const retry = setTimeout(() => setRefsReady((r) => !r), 100);
      return () => clearTimeout(retry);
    }

    const newHeights = {};

    userReplies.forEach((reply) => {
      const orig = originalRefs.current[reply.id];
      const replier = replyRefs.current[reply.id];

      if (orig && replier) {
        const origBox = orig.getBoundingClientRect();
        const replyBox = replier.getBoundingClientRect();
        const distance = replyBox.top - origBox.bottom;

        if (distance > 0 && distance < 1000) {
          newHeights[reply.id] = distance;
        }
      }
    });

    console.log("📏 Line Heights Final:", newHeights);
    setLineHeights(newHeights);
  }, [userReplies, refsReady]);

// ✅ New insert goes here
useEffect(() => {
  const storedUser = JSON.parse(localStorage.getItem("currentUser"));
  if (storedUser && !currentUser) {
    setLocalCurrentUser(storedUser);
    if (typeof setCurrentUser === "function") {
      setCurrentUser(storedUser);
    }
    console.log("✅ Restored currentUser in Profile:", storedUser);
  }
}, [currentUser, setCurrentUser]);


useEffect(() => {
  const fetchUserData = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser || !storedUser.username) {
        console.error("❌ No logged-in user found in localStorage.");
        return;
      }

      const profileUsername = username || storedUser.username;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/${profileUsername}`);
      if (!response.ok) throw new Error("Failed to fetch user data.");

      const userData = await response.json();

     const getImageUrl = (path, type) => {
  const defaultThumbnail = `${process.env.REACT_APP_PUBLIC_URL}/images/default-thumbnail.jpg`;
const defaultBanner = `${process.env.REACT_APP_PUBLIC_URL}/images/default-banner.jpg`;


  if (!path || path === "null" || path === "undefined") {
    return type === "banner" ? defaultBanner : defaultThumbnail;
  }

  const cleanPath = path.replace(/^\/+/, "").replace(/^uploads\/uploads/, "uploads");

  // ✅ Handle known default image filenames stored in DB
  if (cleanPath === "images/default-thumbnail.jpg") return defaultThumbnail;
  if (cleanPath === "images/default-banner.jpg") return defaultBanner;

  // ✅ Absolute URLs stay untouched
  if (cleanPath.startsWith("http")) return cleanPath;

  // ✅ Uploaded files
  if (cleanPath.startsWith("uploads/")) return `${process.env.REACT_APP_API_URL}/${cleanPath}`;


  // ✅ Default image paths
  if (cleanPath.startsWith("images/")) return `${process.env.REACT_APP_PUBLIC_URL}/${cleanPath}`;


  // ✅ Fallback
  return type === "banner" ? defaultBanner : defaultThumbnail;
};


const fullImageUrl = getImageUrl(userData.thumbnail, "thumbnail");
const fullBackgroundUrl = getImageUrl(userData.background, "banner");



      // ✅ Merge original storedUser to preserve group_id and other session info
      const updatedUser = {
        ...storedUser, // Preserve group_id and other session info
        ...userData,   // Overwrite with latest user profile data
        thumbnail: fullImageUrl,
        background: fullBackgroundUrl,
      };

      // ✅ Update local state and session
      setLocalCurrentUser(updatedUser);
      setProfilePicture(fullImageUrl);
      setProfileBackground(fullBackgroundUrl);
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));

      if (typeof setCurrentUser === "function") {
        setCurrentUser(updatedUser);
      }

      // ✅ Fetch posts after state updates
      fetchProfilePosts(profileUsername);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${profileUsername}/replies`);
      if (!res.ok) throw new Error("Failed to fetch replies.");
      const replyData = await res.json();

      const formattedReplies = replyData.map((reply) => {
        const replyUserThumb = reply.thumbnail?.startsWith("http")
          ? reply.thumbnail
          : `${process.env.REACT_APP_API_URL}${reply.thumbnail || "/images/default-thumbnail.jpg"}`;

        const postThumb = reply.originalPost?.thumbnail?.startsWith("http")
          ? reply.originalPost.thumbnail
          : `${process.env.REACT_APP_API_URL}${reply.originalPost?.thumbnail || "/images/default-thumbnail.jpg"}`;

        return {
          ...reply,
          thumbnail: replyUserThumb,
          media_url: reply.media_url
            ? (reply.media_url.startsWith("http")
                ? reply.media_url
                : `${process.env.REACT_APP_API_URL}${reply.media_url}`)
            : null,
          gif: reply.gif_url || reply.gif || null,
          originalPost: {
            ...reply.originalPost,
            thumbnail: postThumb,
          },
        };
      });

      setUserReplies(formattedReplies);

      // 🔵 Also fetch follower/following counts
      fetchFollowCounts(updatedUser.id);


    } catch (error) {
      console.error("❌ Error fetching user data:", error);
    }
  };

  fetchUserData();
}, [username]);

  const fetchFollowCounts = async (userId) => {
  try {
    const followersResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/followers/${userId}`);
const followersData = await followersResponse.json();

const followingResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/following/${userId}`);
const followingData = await followingResponse.json();


    setFollowerCount(Array.isArray(followersData) ? followersData.length : 0);
    setFollowingCount(Array.isArray(followingData) ? followingData.length : 0);

  } catch (error) {
    console.error("Error fetching follower/following counts:", error);
  }
};

const openFollowModal = async (type) => {
  try {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    if (!storedUser?.username) {
      console.error("❌ No current user in localStorage, cannot open modal");
      return;
    }

    // ✅ Always refresh current user from backend to get correct numeric ID
    const profileRes = await fetch(`${process.env.REACT_APP_API_URL}/profile/${storedUser.username}`);
    if (!profileRes.ok) throw new Error("Failed to refresh current user before modal");
    const freshUser = await profileRes.json();

    const profileOwnerId = freshUser.id;
    console.log(`🔄 Opening ${type} modal for userId:`, profileOwnerId);

    // ✅ FIX: Use the same working route style as Home page
    const endpoint =
      type === "followers"
        ? `${process.env.REACT_APP_API_URL}/api/followers/${profileOwnerId}`
        : `${process.env.REACT_APP_API_URL}/api/following/${profileOwnerId}`;

    const res = await fetch(endpoint);
    if (!res.ok) throw new Error(`Failed to fetch ${type} list`);
    const listData = await res.json();

    // ✅ Fetch viewer’s own following list to tag Follow/Following correctly
    const viewerFollowingRes = await fetch(`${process.env.REACT_APP_API_URL}/api/following/${freshUser.id}`);
    const viewerFollowing = viewerFollowingRes.ok ? await viewerFollowingRes.json() : [];
    const viewerFollowingIds = viewerFollowing.map((entry) => entry.id); // ✅ flat user list

    // ✅ FIX: followers still expect followerUser, following is just a flat user
    const updatedList = listData.map((entry) => {
      if (type === "followers") {
        // Followers still wrapped in followerUser
        return {
          ...entry,
          followerUser: {
            ...entry.followerUser,
            isFollowing: viewerFollowingIds.includes(entry.followerUser.id),
          },
        };
      } else {
        // Following are flat users
        return {
          ...entry,
          isFollowing: viewerFollowingIds.includes(entry.id),
        };
      }
    });

    setFollowModalType(type);
    setFollowModalList(updatedList);
    setShowFollowModal(true);
  } catch (err) {
    console.error(`❌ Error opening ${type} modal:`, err);
  }
};

   const fetchProfilePosts = async (username) => {
  try {
    // ✅ Fetch ALL posts (base dataset for own posts)
    const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`, { credentials: "include" });
    if (!response.ok) throw new Error("Failed to fetch posts.");

    let allPosts = await response.json();
    console.log("🔍 Raw Posts from Database:", allPosts);

    // --------------------------------------------
    // ⭐ NORMALIZER used consistently for ALL routes
    // --------------------------------------------
    const normalize = (post) => ({
      ...post,

      // ❤️ Likes — handle all possible backend patterns
      likes:
        post.likes ??
        post.like_count ??
        (Array.isArray(post.likesList) ? post.likesList.length : 0) ??
        0,

      // 💬 Replies — fully normalized
      repliesCount:
        post.repliesCount ??
        post.reply_count ??
        (Array.isArray(post.replies) ? post.replies.length : 0) ??
        0,

      // 🔁 Reposts
      repostsCount:
        post.repostsCount ??
        post.repost_count ??
        (Array.isArray(post.reposts) ? post.reposts.length : 0) ??
        0,

      // 🖼 Thumbnail
      thumbnail: post.thumbnail?.startsWith("http")
        ? post.thumbnail
        : `${process.env.REACT_APP_API_URL}${
            post.thumbnail || "/images/default-thumbnail.jpg"
          }`,

      // 🎞️ Media
      media_url: post.media_url
        ? `${process.env.REACT_APP_API_URL}${post.media_url}`
        : null,

      gif: post.gif_url || null,
    });

    // --------------------------------------------
    // ⭐ USER'S OWN POSTS (SORTED)
    // --------------------------------------------
    const userPosts = allPosts
      .filter((p) => p.username === username)
      .map(normalize)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));  // NEW

    // --------------------------------------------
    // ⭐ LIKED POSTS (SORTED)
    // --------------------------------------------
    const likedRes = await fetch(
      `${process.env.REACT_APP_API_URL}/users/${username}/liked-posts`
    );
    const likedRaw = likedRes.ok ? await likedRes.json() : [];
    const liked = likedRaw
      .map(normalize)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));  // NEW

    // --------------------------------------------
    // ⭐ REPOSTED POSTS (SORTED)
    // --------------------------------------------
    const repostRes = await fetch(
      `${process.env.REACT_APP_API_URL}/users/${username}/reposts`
    );
    const repostRaw = repostRes.ok ? await repostRes.json() : [];
    const reposted = repostRaw
      .map(normalize)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));  // NEW

    // --------------------------------------------
    // ⭐ STORE RESULTS IN CORRECT STATES
    // --------------------------------------------
    setProfilePosts(userPosts);
    setLikedPosts(liked);
    setUserReposts(reposted);

    console.log("📸 User Posts:", userPosts);
    console.log("❤️ Liked Posts:", liked);
    console.log("🔁 Reposted Posts:", reposted);

  } catch (err) {
    console.error("❌ Error in fetchProfilePosts:", err);
  }
};


   // ✅ Fixes Image Upload Not Updating

   const handleProfileImageUpload = async (event) => { 
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("thumbnail", file);  // ✅ Correct field name

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/${currentUser.username}`, {
            method: "PUT",
            body: formData,
        });

        if (!response.ok) throw new Error("Failed to update profile picture.");

        const data = await response.json();
        console.log("✅ Profile picture updated:", data.thumbnail);

        const newThumbnail = `${process.env.REACT_APP_API_URL}${data.thumbnail}?t=${Date.now()}`;
const newBackground = data.background
  ? `${process.env.REACT_APP_API_URL}${data.background}?t=${Date.now()}`
  : "";

// ✅ Update State for Profile Picture and Background
setProfilePicture(newThumbnail);
setProfileBackground(newBackground); // ← make sure you define this state!
setImageKey(Date.now());

// ✅ Update Local Storage with both values
const updatedUser = {
  ...currentUser,
  thumbnail: newThumbnail,
  background: newBackground,
};
localStorage.setItem("currentUser", JSON.stringify(updatedUser));

// ✅ Propagate to Sidebar and Global State
setLocalCurrentUser(updatedUser);
setCurrentUser(updatedUser);


        // ✅ Refresh profile posts so they use the new image
        fetchProfilePosts(currentUser.username);
    } catch (error) {
        console.error("❌ Error updating profile picture:", error);
    }
};

const handleProfileSearch = (query) => {
    setProfileSearchQuery(query);
    
    if (!query || query.trim() === "") {
        // ✅ Reset back to all posts if search is cleared
        fetchProfilePosts(currentUser?.username);
        return;
    }

    // ✅ Lowercase for case-insensitive comparison
    const lowerQuery = query.toLowerCase().trim();

    // ✅ Apply Local Filtering First
    const filteredPosts = profilePosts.filter(post =>
        post.content.toLowerCase().includes(lowerQuery) ||
        (post.username && post.username.toLowerCase().includes(lowerQuery))
    );

    // ✅ If we already have posts, filter locally
    if (filteredPosts.length > 0) {
        setProfilePosts(filteredPosts);
    } else {
        // ✅ If no matches locally, fetch from backend
        fetchProfilePosts(currentUser?.username, query);
    }
};


    

    const handleProfilePost = async () => { 
        if (!profilePostContent.trim() && !selectedFile) {
            alert("Post content or an image is required.");
            return;
        }
        if (!currentUser || !currentUser.username) {
            alert("No user logged in.");
            return;
        }

        const formData = new FormData();
        formData.append("content", profilePostContent || "📸 Uploaded Media");
        if (selectedFile) {
            formData.append("file", selectedFile);
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`, {

                method: "POST",
                credentials: "include",
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to create post.");
            }

            const newPost = await response.json();
            setProfilePosts(prevPosts => [
                {
                    ...newPost,
                    media_url: newPost.media_url
                        ? `${process.env.REACT_APP_API_URL}${newPost.media_url}?t=${Date.now()}`
                        : null,
                    thumbnail: newPost.thumbnail
                        ? `${process.env.REACT_APP_API_URL}${newPost.thumbnail}?t=${Date.now()}`
                        : "/images/default-thumbnail.jpg",
                },
                ...prevPosts, 
            ]);

            setProfilePostContent("");  
            setSelectedFile(null);
        } catch (error) {
            console.error("❌ Error creating post:", error);
        }
    };
    const openReplyDialog = (postId) => {
        console.log(`Open reply dialog for post ${postId}`);
        // ✅ Future: Implement a modal to show replies
    };
    const [dropdownOpen, setDropdownOpen] = useState(null);

// ✅ Toggle Dropdown Visibility
const toggleDropdown = (postId) => {
    setDropdownOpen(dropdownOpen === postId ? null : postId);
};

// ✅ Handle Post Deletion

const handleDeletePost = async (postId) => {
  if (!window.confirm("Are you sure you want to delete this post?")) return;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postId}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) throw new Error("Failed to delete post.");

    setProfilePosts(prevPosts => prevPosts.filter(post => post.id !== postId));
  } catch (error) {
    console.error("❌ Error deleting post:", error);
  }
};
const openPostModal = (post) => {
  setSelectedPost(post);
  setShowPostModal(true);
};
const handleLike = (postId) => {
  console.log(`👍 Like clicked for post ${postId} (profile modal)`);
};

const handleRepost = (postId) => {
  console.log(`🔁 Repost clicked for post ${postId} (profile modal)`);
};


// ✅ Placeholder for Report Function
const reportPost = (postId) => {
  alert(`Reported post ${postId}. This feature will be implemented soon.`);
};

const handleUserFollowToggle = async (targetUserId, currentlyFollowing) => {
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  if (!currentUser) return;

  console.log("🔁 Follow toggle clicked from modal:", {
    targetUserId,
    currentlyFollowing,
    viewer: currentUser.id,
  });

  try {
    const endpoint = currentlyFollowing
      ? "/api/unfollow"
      : "/api/follow";

    await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        followedId: targetUserId,
      }),
    });

    // ✅ Update modal list visually
    setFollowModalList((prevList) =>
      prevList.map((entry) => {
        const u =
          followModalType === "followers"
            ? entry.followerUser
            : entry.followingUser;

        if (u?.id === targetUserId) {
          const updatedUser = { ...u, isFollowing: !currentlyFollowing };
          return {
            ...entry,
            [followModalType === "followers" ? "followerUser" : "followingUser"]: updatedUser,
          };
        }
        return entry;
      })
    );

    // ✅ ALSO refresh the profile header counts after toggle
    fetchFollowCounts(currentUser.id);

  } catch (err) {
    console.error("❌ Error toggling follow in modal:", err);
  }
};


    return (
        <div className="profile-page-container">
            <div className="profile-page-header">
              <h1>Your Profile</h1>
            </div>

<>
  <div className="profile-header-container">
  {/* ✅ Background logic unchanged (already works) */}
  <div
    className="profile-banner"
    style={{
      backgroundImage: `url("${profileBackground || "/images/default-banner.jpg"}")`,
    }}
  ></div>

  {/* ✅ Thumbnail fixed to always match same default logic */}
  <div className="profile-thumbnail-wrapper">
    <img
      key={imageKey}
      src={
        profilePicture && profilePicture.startsWith("http")
          ? profilePicture
          : "/images/default-thumbnail.jpg"
      }
      alt="Profile"
      className="profile-thumbnail"
      onError={(e) => (e.target.src = "/images/default-thumbnail.jpg")}
    />
  </div>

  <div className="profile-details">
    <h2 className="profile-page-username">
  {currentUser?.name && (
    <>
      <span className="profile-realname">{currentUser.name}</span>

      {currentUser?.Group?.showBadge && (
        <span
          title={currentUser.Group.badgeLabel}
          style={{
                marginLeft: "0px",
                marginRight: "2px",
                display: "inline-block",
                lineHeight: "1",
                position: "relative",
                top: "-1px",
              }}
        >
          {currentUser.Group.badgeType === "circle" && (
            <span
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                backgroundColor: "#000",
                color: "#d4af37",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {currentUser.Group.badgeText}
            </span>
          )}

          {currentUser.Group.badgeType === "star" && (
            <span
              style={{
                color:
                  currentUser.Group.badgeColor === "silver"
                    ? "#C0C0C0"
                    : currentUser.Group.badgeColor === "gold"
                    ? "#FFD700"
                    : currentUser.Group.badgeColor === "platinum"
                    ? "#b9c3cf"
                    : "#4da6ff",
                fontSize: "20px",
              }}
            >
              ★
            </span>
          )}

          {currentUser.Group.badgeType === "diamond" && (
            <span style={{ color: "#4fd1ff", fontSize: "20px" }}>
              ♦
            </span>
          )}
        </span>
      )}
    </>
  )}

  <span className="profile-username">@{currentUser?.username || "Loading..."}</span>
</h2>

    <p className="profile-page-bio">
      {currentUser?.bio || "This is the user's bio."}
    </p>

    <div className="profile-follow-counts">
      <span
        className="profile-follow-count"
        onClick={() => openFollowModal("followers")}
      >
        <strong>{followerCount}</strong> Followers
      </span>
      <span style={{ margin: "0 10px" }}>|</span>
      <span
        className="profile-follow-count"
        onClick={() => openFollowModal("following")}
      >
        <strong>{followingCount}</strong> Following
      </span>
    </div>

    <input
      type="file"
      accept="image/*"
      style={{ display: "none" }}
      ref={fileInputRef}
      onChange={handleProfileImageUpload}
    />
    <button
      className="edit-profile-image-btn"
      onClick={() => setShowEditDialog(true)}
    >
      Edit Profile
    </button>
  </div>
</div>

<div className="profile-action-bar">
  <button
    className={filterType === "all" ? "active-filter" : ""}
    onClick={() => setFilterType("all")}
  >
    Posts ({profilePosts.length})
  </button>

  <button
    className={filterType === "likes" ? "active-filter" : ""}
    onClick={() => setFilterType("likes")}
  >
    Likes ({likedPosts.length})
  </button>

  <button
    className={filterType === "replies" ? "active-filter" : ""}
    onClick={() => setFilterType("replies")}
  >
    Replies ({userReplies.length})
  </button>

  <button
    className={filterType === "reposts" ? "active-filter" : ""}
    onClick={() => setFilterType("reposts")}
  >
    Reposts ({userReposts.length})
  </button>

  <button
    className={filterType === "media" ? "active-filter" : ""}
    onClick={() => setFilterType("media")}
  >
    Media ({profilePosts.filter(p => p.media_url || p.gif).length})
  </button>
</div>
</>

<input
  type="text"
  className="profile-search-box"   // 🔹 change this
  placeholder="Search your posts..."
  value={profileSearchQuery}
  onChange={(e) => handleProfileSearch(e.target.value)}
/>


<section className="profile-page-posts">
  <h2>
    {filterType === "all" && "Your Posts"}
    {filterType === "likes" && "Liked Posts"}
    {filterType === "replies" && "Your Replies"}
    {filterType === "reposts" && "Reposted Posts"}
    {filterType === "media" && "Media Posts"}
  </h2>

  
  {filterType === "replies" && (
  <div className="profile-replies-section">
    {userReplies.length === 0 ? (
      <p>No replies yet.</p>
    ) : (
      userReplies.map((reply) => (
        <div className="reply-wrapper" key={reply.id}>
          <div
            className="profile-page-post reply-combined-box"
            style={{ position: "relative" }}
          >
            {/* 🔴 Connector Line */}
            {lineHeights[reply.id] && (
              <div
                className="profile-reply-connector"
                style={{ height: `${Math.max(0, lineHeights[reply.id] - 5)}px` }}
                aria-hidden="true"
              ></div>
            )}

           {/* 🔹 Original Post */}
<div className="profile-post-header">
  <img
    ref={(el) => (originalRefs.current[reply.id] = el)}
    src={
      reply.originalPost?.thumbnail ||
      "/images/default-thumbnail.jpg"
    }
    alt="Original poster"
    className="profile-post-thumbnail"
  />
  <div className="profile-post-header-block">
    <div className="profile-post-header-line">
      {/* ✅ Real name first if available */}
      <span className="profile-post-realname">
        {reply.originalPost?.postUser?.name ||
          reply.originalPost?.name ||
          "Unknown"}
      </span>
      <span className="profile-post-username">
        @{reply.originalPost?.username || "unknown"}
      </span>
      <span className="profile-post-date">
  {reply.originalPost?.createdAt
    ? timeAgo(reply.originalPost.createdAt)
    : ""}
</span>

    </div>
    <p className="profile-post-text">
      {reply.originalPost?.content.length > 300 ? (
        <>
          {reply.originalPost.content.slice(0, 300)}...
          <button
            className="show-more-btn"
            onClick={() => {
              setSelectedPost(reply.originalPost);
              setShowPostModal(true);
            }}
            style={{
              marginLeft: "6px",
              fontSize: "0.9rem",
              background: "none",
              border: "none",
              color: "#4267B2",
              cursor: "pointer",
            }}
          >
            Show more
          </button>
        </>
      ) : (
        reply.originalPost?.content
      )}
    </p>
  </div>
</div>

{reply.originalPost?.media_url &&
  (reply.originalPost.media_url.endsWith(".mp4") ? (
    <video
      controls
      className="profile-post-media"
      src={`${process.env.REACT_APP_API_URL}${reply.originalPost.media_url}`}
    />
  ) : (
    <img
      className="profile-post-media"
      src={`${process.env.REACT_APP_API_URL}${reply.originalPost.media_url}`}
      alt="Original post media"
    />
  ))}

{reply.originalPost?.gif && (
  <img
    src={reply.originalPost.gif}
    className="profile-post-gif"
    alt="GIF"
  />
)}

{/* 🔹 Replying User */}
<div className="profile-post-header reply-spacing">
  <img
    ref={(el) => (replyRefs.current[reply.id] = el)}
    src={
      reply.thumbnail?.startsWith("http")
        ? reply.thumbnail
        : `${process.env.REACT_APP_API_URL}${
            reply.thumbnail || "/images/default-thumbnail.jpg"
          }`
    }
    alt="Reply user"
    className="profile-post-thumbnail"
  />
  <div className="profile-post-header-block">
    <div className="profile-post-header-line">
      {/* ✅ Show reply user’s real name if available */}
      <span className="profile-post-realname">
        {reply.postUser?.name || reply.name || "Unknown"}
      </span>
      <span className="profile-post-username">@{reply.username}</span>
      <span className="profile-post-date">
  {timeAgo(reply.createdAt)}
</span>

    </div>
    <p className="profile-post-text">{reply.content}</p>
  </div>
</div>

{reply.gif && (
  <img
    src={reply.gif}
    className="profile-post-gif"
    alt="Reply GIF"
  />
)}

{reply.media_url &&
  (reply.media_url.endsWith(".mp4") ||
  reply.media_url.endsWith(".webm") ||
  reply.media_url.endsWith(".ogg") ? (
    <video controls className="profile-post-media">
      <source src={reply.media_url} />
      Your browser does not support the video tag.
    </video>
  ) : (
    <img
      src={reply.media_url}
      alt="Reply media"
      className="profile-post-media"
    />
  ))}

            {/* 🔹 Action Bar */}
            <div className="profile-post-actions">
              <button>❤ {reply.likes || 0}</button>
              <button>🔁 {reply.repost_count || 0}</button>
              <button onClick={() => openReplyDialog(reply.id)}>💬 0</button>

              <div
                className="post-dropdown"
                onMouseEnter={() => setDropdownOpen(reply.id)}
                onMouseLeave={() => setDropdownOpen(null)}
              >
                <button className="ellipsis-button">⋮</button>
                {dropdownOpen === reply.id && (
                  <div className="dropdown-menu">
                    <button onClick={() => handleDeletePost(reply.id)}>
                      🗑 Delete
                    </button>
                    <button onClick={() => reportPost(reply.id)}>
                      🚩 Report
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
)}

  {/* ✅ Regular Posts, Likes, Reposts, Media */}
  <div className="profile-page-posts-list">
  {(
    filterType === "likes"
      ? likedPosts
      : filterType === "reposts"
      ? userReposts
      : profilePosts
  ).length > 0 ? (
    (
      filterType === "likes"
        ? likedPosts
        : filterType === "reposts"
        ? userReposts
        : profilePosts
    )
      .filter((post) => {
        switch (filterType) {
          case "replies":
            return post.repliesCount > 0;
          case "reposts":
            return true;
          case "media":
            return post.media_url || post.gif;
          default:
            return true;
        }
      })
      .map((post) => (
        <div key={post.id} className="profile-page-post">
          <div className="profile-post-header">
            <img
              src={post.thumbnail || "/images/default-thumbnail.jpg"}
              alt={post.username}
              className="profile-post-thumbnail"
            />
            <div className="profile-post-header-block">
              <div className="profile-post-header-line">
       <div className="profile-user-block">
  <span className="profile-post-realname">
    {post.postUser?.name || currentUser?.name}
  </span>

  {post.postUser?.Group?.showBadge && (
    <span
      title={post.postUser?.Group?.badgeLabel}
      style={{
        marginLeft: "4px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        verticalAlign: "baseline",
        lineHeight: "1",
        position: "relative",
        top: "-1px",
      }}
    >
      {post.postUser?.Group?.badgeType === "circle" && (
        <span
          style={{
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            backgroundColor: "#000",
            color: "#d4af37",
            fontSize: "11px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {post.postUser?.Group?.badgeText}
        </span>
      )}

      {post.postUser?.Group?.badgeType === "star" && (
        <span
          style={{
            color:
              post.postUser?.Group?.badgeColor === "gold"
                ? "#d4af37"
                : post.postUser?.Group?.badgeColor,
            fontSize: "16px",
          }}
        >
          ★
        </span>
      )}

      {post.postUser?.Group?.badgeType === "diamond" && (
        <span
          style={{
            color: "#4fc3f7",
            fontSize: "14px",
          }}
        >
          ♦
        </span>
      )}
    </span>
  )}

  <span className="profile-post-username">
    @{post.username}
  </span>
</div>
          <span className="profile-post-date">
  {timeAgo(post.createdAt)}
</span>

      </div>

              {post.content.length > 300 ? (
  <>
    <p className="profile-post-text">
      {post.content.slice(0, 300)}...
    </p>
    <button
      className="show-more-btn"
      onClick={() => openPostModal(post)}
    >
      Show more
    </button>
  </>
) : (
  <p className="profile-post-text">{post.content}</p>
)}

            </div>
          </div>

          {post.gif && (
            <img
              src={post.gif}
              alt="Posted GIF"
              className="profile-post-gif"
            />
          )}

          {post.media_url &&
            (post.media_url.endsWith(".mp4") ||
            post.media_url.endsWith(".webm") ||
            post.media_url.endsWith(".ogg") ? (
              <video controls className="profile-post-media">
                <source src={post.media_url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={post.media_url}
                alt="Uploaded media"
                className="profile-post-media"
              />
            ))}

          <div className="profile-post-actions">
            <button>❤ {post.likes || 0}</button>
            <button>🔁 {post.repostsCount ?? post.repost_count ?? 0}</button>
            <button onClick={() => openReplyDialog(post.id)}>
              💬 {post.repliesCount || 0}
            </button>

            <div
              className="post-dropdown"
              onMouseEnter={() => setDropdownOpen(post.id)}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button className="ellipsis-button">⋮</button>
              {dropdownOpen === post.id && (
                <div className="dropdown-menu">
                  <button onClick={() => handleDeletePost(post.id)}>
                    🗑 Delete
                  </button>
                  <button onClick={() => reportPost(post.id)}>
                    🚩 Report
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))
  ) : (
    <p>No posts found.</p>
  )}
</div>

</section>
{showPostModal && selectedPost && (
  <PostDetailModal
    post={selectedPost}
    onClose={() => setShowPostModal(false)}
    onReply={(post) => {
      setShowPostModal(false);
      setTimeout(() => openReplyDialog(post.id), 100);
    }}
    onLike={() => handleLike(selectedPost.id)}
    onRepost={() => handleRepost(selectedPost.id)}
    onNavigateToProfile={(username) => {
      window.location.href = `/@${username}`;
    }}
  />
)}

{showEditDialog && (
  <ProfileEditDialog
    user={currentUser}
    onClose={() => setShowEditDialog(false)}
    onSave={async (formData) => {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser?.username) {
        console.error("❌ No user in localStorage. Cannot update.");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${storedUser.username}`, {
          method: "PUT",
          body: formData,
        });

        if (!response.ok) throw new Error("Failed to update profile");

        const updatedUserData = await response.json();
        const updatedUser = updatedUserData.user || updatedUserData;

        // ✅ Prepare new thumbnail
        const newThumbnail = updatedUser.thumbnail?.startsWith("http")
          ? updatedUser.thumbnail
          : `${process.env.REACT_APP_API_URL}${updatedUser.thumbnail}`;

        setProfilePicture(newThumbnail);
        setImageKey(Date.now());

        // ✅ Prepare new background
        const newBackground = updatedUser.background?.startsWith("http")
          ? updatedUser.background
          : `${process.env.REACT_APP_API_URL}${updatedUser.background}`;

        setProfileBackground(newBackground); // ✅ Needed for immediate update

        // ✅ Store updated user data
        const updatedUserWithMedia = {
          ...updatedUser,
          thumbnail: newThumbnail,
          background: newBackground,
        };

        localStorage.setItem("currentUser", JSON.stringify(updatedUserWithMedia));
        setLocalCurrentUser(updatedUserWithMedia);
        setCurrentUser(updatedUserWithMedia);

        setShowEditDialog(false);
        alert("✅ Profile updated!");
      } catch (err) {
        console.error("❌ Error updating profile:", err);
        alert("Failed to update profile.");
      }
    }}
  />
)}

{showFollowModal && (
  <div className="follow-modal-overlay">
    <div className="follow-modal">
      <h3>{followModalType === "followers" ? "Followers" : "Following"}</h3>
      <button
        className="close-follow-modal"
        onClick={() => setShowFollowModal(false)}
      >
        Close
      </button>
      <ul className="follow-user-list">
        {followModalList.map((entry) => {
          // ✅ For followers, user is wrapped in followerUser; for following, it's a flat user
          const u = followModalType === "followers" ? entry.followerUser : entry;

          if (!u) return null;

          const thumbnail = u.thumbnail?.startsWith("http")
            ? u.thumbnail
            : `${process.env.REACT_APP_API_URL}${u.thumbnail || "/images/default-thumbnail.jpg"}`;

          return (
            <li key={u.id} className="follow-user-item">
              <img
                src={thumbnail}
                alt={u.username}
                className="follow-user-thumbnail"
              />
              <div className="follow-user-details">
                <span className="follow-user-name">{u.name || "No name"}</span>
                <span className="follow-user-username">@{u.username}</span>
              </div>
              {currentUser?.id !== u.id && (
                <button
                  className={`follow-user-action-button ${u.isFollowing ? "following" : ""}`}
                  onClick={() => handleUserFollowToggle(u.id, u.isFollowing)}
                  onMouseEnter={(e) => {
                    if (u.isFollowing) e.target.textContent = "Unfollow";
                  }}
                  onMouseLeave={(e) => {
                    if (u.isFollowing) e.target.textContent = "Following";
                  }}
                >
                  {u.isFollowing ? "Following" : "Follow"}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </div>
)}



</div>
);
}

export default Profile;

